"use client";

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  /** Mensagem amigável que substitui o componente quebrado. */
  fallback?: React.ReactNode;
  /** Label do botão de retry. */
  resetLabel?: string;
  /** Marca a área isolada — só usado pra debug. */
  scope?: string;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

/**
 * Pega erros de render de uma subárvore sem deixar o resto da aplicação
 * cair. Útil pra envolver o preview ao vivo do wizard e seções
 * experimentais que dependem de dados do usuário potencialmente malformados
 * (experienceData de rascunho antigo, fotos parcialmente uploadadas, etc).
 *
 * Quando dá erro, mostra um card discreto + um botão pra tentar de novo
 * (limpa o estado interno do boundary). O resto da tela continua usável.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? undefined });
    // Log no console é o que sobra com source maps desligados em prod —
    // o usuário pode mandar a mensagem direto pra gente.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.scope ? ` · ${this.props.scope}` : ""}]`,
      error,
      info.componentStack
    );
  }

  reset = () =>
    this.setState({ hasError: false, error: undefined, componentStack: undefined });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    // Primeira linha não-vazia do componentStack — geralmente revela
    // qual componente React quebrou (ex: "in TimelineSection (...)").
    const firstStackLine = this.state.componentStack
      ?.split("\n")
      .map((s) => s.trim())
      .filter((s) => s.startsWith("in "))[0];

    return (
      <div
        className="rounded-2xl border bg-white p-5 flex flex-col items-center justify-center text-center gap-3"
        style={{
          borderColor: "rgba(245,158,11,0.25)",
          background: "rgba(245,158,11,0.04)",
          minHeight: 200,
        }}
      >
        <div className="grid h-10 w-10 place-items-center rounded-full bg-amber-100 text-amber-700">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <p className="text-sm font-semibold text-[#1F2937]">
          Algo deu errado nessa área.
        </p>
        {this.state.error?.message && (
          <p className="text-xs text-gray-700 max-w-xs font-mono leading-relaxed">
            {this.state.error.message}
          </p>
        )}
        {firstStackLine && (
          <p className="text-[10px] text-gray-400 max-w-xs font-mono leading-relaxed break-all">
            {firstStackLine}
          </p>
        )}
        {this.props.scope && (
          <p className="text-[10px] text-gray-300 uppercase tracking-widest">
            scope: {this.props.scope}
          </p>
        )}
        <button
          type="button"
          onClick={this.reset}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#E11D48] px-4 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {this.props.resetLabel || "Tentar de novo"}
        </button>
      </div>
    );
  }
}
